import Badge from 'atomic/badge';
import Link from 'atomic/link';
import NumberOfOffer from 'atomic/number-of-offer';
import Rating from 'atomic/rating';
import ProductListImpression from 'components/impressions/product-impression/list/product-list-impression';
import { approximateNumber } from 'helpers/approximate-number';
import React, { useState } from 'react';
import { InView } from 'react-intersection-observer';
import WidgetProductSliderProps from '../widget-product-slider.props';
import WidgetProductSliderDesktopBasic from './components/widget-product-slider-desktop-basic';
import AlertDiscountPriceInfo from 'modules/shared/components/alert-discount-price-info';
import WidgetAttributesTable from 'modules/shared/widgets/shared/widget-attributes-table';
import LinkedWrapper from 'atomic/linked-wrapper';
import { prepareOfferDataForDiscount } from 'modules/shared/helpers/prepare-offer-data-for-discount';
import ProductBoxPrice from 'modules/shared/components/product/components/product-box-price';
import { isMonoOffer } from 'helpers/offers-prepare';
import OfferLabel from 'modules/shared/modules/product/components/offer-label';
import { getMonoOfferLabel } from 'helpers/offer-label';

import './widget-product-slider.desktop.scss';

const WidgetProductSliderDesktop: React.FC<WidgetProductSliderProps> = ({ config, data }) => {
    const [WidgetProductSliderComponent, setWidgetProductSliderComponent] = useState<React.ComponentType<any>>(() => WidgetProductSliderDesktopBasic);
    const { items, count, category } = data;

    config.lazyLoad = config.lazyLoad ?? true;
    config.autoPlay = config.autoPlay ?? false;
    config.itemsOnScreen = config.itemsOnScreen ?? 5;

    const onView = (inView: boolean): void => {
        if (inView) {
            import('react-multi-carousel').then(mod => {
                setWidgetProductSliderComponent(() => mod.default);
            });
        }
    };

    const responsive = {
        desktop: {
            breakpoint: { max: 5000, min: 0 },
            items: config.itemsOnScreen
        }
    };

    return (
        <div className="w-product-slider-d">
            <InView triggerOnce={false} as="div" onChange={onView}>
                <WidgetProductSliderComponent
                    responsive={responsive}
                    swipeable={false}
                    draggable={false}
                    slidesToSlide={config.itemsOnScreen}
                    renderButtonGroupOutside={true}
                    arrows={true}
                    autoPlay={config.autoPlay}
                    className="w-product-slider-d__list"
                    itemClass={`w-product-slider-d__list__item w-${config.itemsOnScreen}`}
                >
                    {
                        items.map(product => {
                            const {
                                discount,
                                minPrice30Days,
                                price,
                                offersCount,
                                prevPrice,
                                showDiscount
                            } = prepareOfferDataForDiscount(
                                product.minPrice,
                                product.prevPrice,
                                product.offers.items,
                                product.offers.count
                            );

                            const offerLabel = getMonoOfferLabel(product);

                            return (
                                <LinkedWrapper
                                    key={product.id}
                                    className="w-product-slider-d__list__item-content"
                                    title={isMonoOffer(product) ? product.offers.items[0].dealer.name : product.name}
                                    href={isMonoOffer(product) ? product.offers.items[0].url : product.url}
                                >
                                    <ProductListImpression
                                        trackedData={{ ...product, price }}
                                        className={`w-product-slider-d__list__item-content__image h-${config.itemsOnScreen}`}
                                    >
                                        <img
                                            src={product.photo}
                                            alt={product.name}
                                            title={product.name}
                                            loading={config.lazyLoad ? 'lazy' : 'eager'}
                                            className={`h-${config.itemsOnScreen}`}
                                        />
                                        {offerLabel && (
                                            <div className="w-product-slider-d__list__item-content__image__label">
                                                <OfferLabel label={offerLabel} />
                                            </div>
                                        )}
                                        <div className="w-product-slider-d__list__item-content__image__badges">
                                            {showDiscount && (
                                                <Badge color="red">
                                                    <span>{discount}%</span>
                                                </Badge>
                                            )}
                                        </div>
                                    </ProductListImpression>
                                    <div className="w-product-slider-d__list__item-content__title">
                                        <h3>
                                            <Link
                                                className="w-product-slider-d__list__item-content__link"
                                                href={product.url}
                                            >
                                                {product.name}
                                            </Link>
                                        </h3>
                                    </div>
                                    <div className="w-product-slider-d__list__item-content__stars">
                                        <Rating size="sm" opinions={product.opinions} />
                                    </div>
                                    <div className="w-product-slider-d__list__item-content__price">
                                        <ProductBoxPrice
                                            price={price}
                                            prevPrice={prevPrice}
                                            offersCount={offersCount}
                                            discount={discount}
                                        />
                                    </div>
                                    {showDiscount && (
                                        <div className="w-product-slider-d__list__item-content__discount-info">
                                            <AlertDiscountPriceInfo paddingTop={0} paddingBottom={2} minPrice30Days={minPrice30Days} />
                                        </div>
                                    )}
                                    {!showDiscount && product.attributes && (
                                        <div className="w-product-slider-d__list__item-content__attributes">
                                            <WidgetAttributesTable attributes={product.attributes} rowsNumber={3} />
                                        </div>
                                    )}
                                    <div className="w-product-slider-d__list__item-content__number-of-offer">
                                        <NumberOfOffer offers={product.offers} />
                                    </div>
                                </LinkedWrapper>
                            );
                        })
                    }
                </WidgetProductSliderComponent>
            </InView>
            {category && (
                <div className="w-product-slider-d__footer">
                    <Link href={category.url}
                        type="button"
                        size="lg"
                        color="transparent-secondary"
                        weight="500"
                    >
                        Sprawdź wszystkie ({approximateNumber(count)})
                    </Link>
                </div>
            )}
        </div>
    );
};

export default WidgetProductSliderDesktop;
